/**
 * Replaying a chat result on the What If screen: a run_what_if change set (`c`) or a
 * check_affordability purchase (`buy`, `item`, `on`, `rate`). The result is built with the same
 * functions the MCP tool used (the change set applied by cloud/src/mcp/plan-items.ts, the view from
 * @map-my-money/views), so its result hash equals the chat's.
 */
import {
  affordabilityView, decodeChanges, MAX_CHANGES_PARAM, whatIfView, type AffordabilityResult, type WaypointInput, type WhatIfViewResult,
} from '@map-my-money/views';
import { applyChangeSet, ITEM_KINDS, WRITABLE_KINDS, type PlanChangeSet } from '../../../cloud/src/mcp/plan-items.ts';
import type { WirePlan } from '../api/types.ts';

export type ReplayRequest =
  | { readonly kind: 'none' }
  | { readonly kind: 'changes'; readonly changes: PlanChangeSet }
  | { readonly kind: 'buy'; readonly price: string; readonly item: string; readonly purchaseDate?: string; readonly annualReturnPercent?: string }
  | { readonly kind: 'invalid'; readonly what: 'changes' | 'buy' };

const MAX_ITEMS = 20;
const MAX_FIELDS = 40;
const MAX_TEXT = 200;
const BASE64URL = /^[A-Za-z0-9_-]+$/;
const DECIMAL = /^-?[0-9]+(\.[0-9]+)?$/;
const PRICE = /^\d{1,12}(\.\d{1,2})?$/;
const RATE = /^\d{1,2}(\.\d{1,4})?$/;
const ID = /^.{1,100}$/su;

type Json = Record<string, unknown>;

const isObject = (v: unknown): v is Json => v !== null && typeof v === 'object' && !Array.isArray(v);
const onlyKeys = (o: Json, allowed: readonly string[]) => Object.keys(o).every((k) => allowed.includes(k));
const isId = (v: unknown): v is string => typeof v === 'string' && ID.test(v);

export function isCalendarDay(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

/** Plain JSON, bounded: no deep nesting, no huge strings or arrays. */
function plainJson(v: unknown, depth = 0): boolean {
  if (depth > 4) return false;
  if (v === null || typeof v === 'boolean') return true;
  if (typeof v === 'number') return Number.isFinite(v);
  if (typeof v === 'string') return v.length <= MAX_TEXT;
  if (Array.isArray(v)) return v.length <= 50 && v.every((x) => plainJson(x, depth + 1));
  if (isObject(v)) return Object.keys(v).length <= MAX_FIELDS && Object.values(v).every((x) => plainJson(x, depth + 1));
  return false;
}

const FIELD_CHECKS: Readonly<Record<string, (v: unknown) => boolean>> = {
  id: isId,
  label: (v) => typeof v === 'string' && v.length <= MAX_TEXT,
  type: (v) => typeof v === 'string' && ['income', 'expense', 'investment', 'debt'].includes(v),
  amount: (v) => typeof v === 'string' && DECIMAL.test(v),
  startDate: isCalendarDay,
  endDate: isCalendarDay,
  purchaseDate: isCalendarDay,
  purchasePrice: (v) => typeof v === 'string' && DECIMAL.test(v),
  targetValue: (v) => typeof v === 'string' && DECIMAL.test(v),
  assetClass: (v) => typeof v === 'string' && ['property', 'vehicle', 'electronics', 'furniture', 'other'].includes(v),
  series: (v) => typeof v === 'string' && ['netWorth', 'cash', 'investment', 'debt', 'passive'].includes(v),
  operation: (v) => typeof v === 'string' && ['crossover', 'crossunder', 'equal'].includes(v),
};

const REQUIRED: Readonly<Record<(typeof WRITABLE_KINDS)[number], readonly string[]>> = {
  transaction: ['type', 'amount', 'startDate'],
  asset: ['purchasePrice', 'purchaseDate', 'assetClass', 'valueTrajectory'],
  goal: ['series', 'operation', 'targetValue'],
};

const fieldsValid = (o: Json) => plainJson(o) && Object.entries(o).every(([k, v]) => FIELD_CHECKS[k]?.(v) ?? true);

/** A step's `from`: a day, {in_months}, or {when_ends: id} (R24). */
function validWhen(w: unknown): boolean {
  if (w === undefined || isCalendarDay(w)) return true;
  if (!isObject(w)) return false;
  if (onlyKeys(w, ['in_months']) && 'in_months' in w) return Number.isInteger(w.in_months) && (w.in_months as number) >= 0 && (w.in_months as number) <= 600;
  return onlyKeys(w, ['when_ends']) && isId(w.when_ends);
}

const isPercent = (v: unknown): boolean => v === undefined || (typeof v === 'number' && Number.isFinite(v) && Math.abs(v) <= 1000) || (typeof v === 'string' && DECIMAL.test(v));

function validAddition(a: unknown): boolean {
  if (!isObject(a) || !onlyKeys(a, ['kind', 'item', 'from']) || !validWhen(a.from)) return false;
  const kind = a.kind as (typeof WRITABLE_KINDS)[number];
  if (!WRITABLE_KINDS.includes(kind) || !isObject(a.item)) return false;
  return REQUIRED[kind].every((k) => k in (a.item as Json)) && fieldsValid(a.item);
}

function validUpdate(u: unknown): boolean {
  if (!isObject(u) || !onlyKeys(u, ['id', 'kind', 'set', 'unset', 'from', 'by_percent', 'percent_of_pay', 'income']) || !isId(u.id)) return false;
  if (!validWhen(u.from) || !isPercent(u.by_percent) || !isPercent(u.percent_of_pay) || (u.income !== undefined && !isId(u.income))) return false;
  if (u.kind !== undefined && !(ITEM_KINDS as readonly unknown[]).includes(u.kind)) return false;
  if (u.set !== undefined && !(isObject(u.set) && fieldsValid(u.set))) return false;
  return u.unset === undefined || (Array.isArray(u.unset) && u.unset.length <= 10 && u.unset.every((k) => typeof k === 'string' && k.length <= 40));
}

const validRemoval = (r: unknown): boolean =>
  isObject(r) && onlyKeys(r, ['id', 'kind', 'from']) && isId(r.id) && (r.kind === undefined || (ITEM_KINDS as readonly unknown[]).includes(r.kind)) && validWhen(r.from);

const validRedirect = (r: unknown): boolean => isObject(r) && onlyKeys(r, ['from', 'to', 'when']) && isId(r.from) && isId(r.to) && (r.when === undefined || r.when === 'ends');

const listOf = (v: unknown, valid: (x: unknown) => boolean) => v === undefined || (Array.isArray(v) && v.length <= MAX_ITEMS && v.every(valid));

/** A `c` parameter as the change set run_what_if took, or null when it is oversize or malformed. */
export function parseChangeSet(raw: string): PlanChangeSet | null {
  if (raw.length === 0 || raw.length > MAX_CHANGES_PARAM || !BASE64URL.test(raw)) return null;
  const value = decodeChanges(raw);
  if (!isObject(value) || !onlyKeys(value, ['add', 'update', 'remove', 'redirect', 'timeframe_years'])) return null;
  if (!listOf(value.add, validAddition) || !listOf(value.update, validUpdate) || !listOf(value.remove, validRemoval) || !listOf(value.redirect, validRedirect)) return null;
  const years = value.timeframe_years;
  if (years !== undefined && !(Number.isInteger(years) && (years as number) >= 1 && (years as number) <= 50)) return null;
  return value as PlanChangeSet;
}

/**
 * `from=chat` is a chat result's link; `from=try` is a "Try this" step in the /try playground and
 * `from=move` a check-in's "Your biggest move" (neither has a chat to check against).
 */
export function replayRequest(search: string): ReplayRequest {
  const p = new URLSearchParams(search);
  const from = p.get('from');
  if (from !== 'chat' && from !== 'try' && from !== 'move') return { kind: 'none' };
  const c = p.get('c');
  if (c !== null) {
    const changes = parseChangeSet(c);
    return changes ? { kind: 'changes', changes } : { kind: 'invalid', what: 'changes' };
  }
  const buy = p.get('buy');
  if (buy === null) return { kind: 'none' };
  const item = p.get('item') ?? '';
  const on = p.get('on');
  const rate = p.get('rate');
  const ok = PRICE.test(buy) && Number(buy) > 0 && item.length >= 1 && item.length <= 80
    && (on === null || isCalendarDay(on)) && (rate === null || (RATE.test(rate) && Number(rate) <= 20));
  if (!ok) return { kind: 'invalid', what: 'buy' };
  return { kind: 'buy', price: buy, item, ...(on === null ? {} : { purchaseDate: on }), ...(rate === null ? {} : { annualReturnPercent: rate }) };
}

/** run_what_if's view for this plan, or null when the change set does not apply to it (an id that is gone, say). */
export function replayWhatIf(wire: WirePlan, waypoints: readonly WaypointInput[], asOf: string, changes: PlanChangeSet): WhatIfViewResult | null {
  try {
    const { plan, described } = applyChangeSet(wire, changes, { today: asOf });
    if (described.length === 0) return null;
    return whatIfView(wire, plan, waypoints, asOf, described);
  } catch {
    return null;
  }
}

/** check_affordability's view (without its `item`/`purchase_date` echo, which the hash does not cover). */
export function replayPurchase(wire: WirePlan, waypoints: readonly WaypointInput[], asOf: string, request: Extract<ReplayRequest, { kind: 'buy' }>): AffordabilityResult | null {
  try {
    return affordabilityView(wire, waypoints, asOf, {
      price: request.price,
      item: request.item,
      ...(request.purchaseDate === undefined ? {} : { purchaseDate: request.purchaseDate }),
      ...(request.annualReturnPercent === undefined ? {} : { annualReturnPercent: request.annualReturnPercent }),
    });
  } catch {
    return null;
  }
}
